import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import moment, { Moment } from "moment-timezone";

import { RootState } from "../../appRedux/store";
import { RequestStatus, ParkingRequestTypeConstant } from "../../constants/DatabaseConstants/ParkingConstants";
import * as parkingServices from "../../services/parkingServices";
import * as commonAPI from "../../services/commonServices";
import { CustomNotification } from "../../util/CustomNotification";
import { DefaultPagination } from "../../constants/generalConstants";
import { General } from "../../util/General";
import useValue from "../../hooks/useValue";
import useTablePagination from "../../hooks/useTablePagination";
import { LocalStorageConstants } from "../../constants/ApplicationConstants/LocalStorageConstants";
import LocalStorageUtil from "../../util/LocalStorageUtil";

import type { ReqAckStatusList } from "../../shared/interface/MasterManagement/reqAckStatus.interface";
import type { KeyFobList } from "../../shared/interface/MasterManagement/keyFobMaster.interface";
import type { ParamsObject } from "../../shared/interface/parking.interface";
import type { UserInterface } from "../../shared/interface/auth/User.interface";
import { ExportFile } from "../../util/ExportFile";
import { ErrorMessageConstants } from "../../constants/MessageConstants/ErrorMessageConstants";

const useParkingHistory = () => {
  /* CONSTANT */
  const userData = LocalStorageUtil.localStorageGetItem(LocalStorageConstants.USER_DATA) as UserInterface;

  /* STATE */
  const [mode, setMode] = useState<ParkingRequestTypeConstant>(ParkingRequestTypeConstant.RETRIEVAL);
  const [historyList, setHistoryList] = useState<any[]>([]);
  const [searchedText, setSearchedText] = useState<string>("");
  const [recordID, setRecordID] = useState<number | null>(null);
  const [reFetch, setRefetch] = useState(false);

  /* Filters */
  const [selectedCompletionStatus, setSelectedCompletionStatus] = useState<RequestStatus | undefined>(undefined);
  const [selectedKeyFob, setSelectedKeyFob] = useState<number | null>(null);
  const [selectedReqAckStatus, setSelectedReqAckStatus] = useState<number | null>(null);
  const [dateRange, setDateRange] = useState<{
    fromDate: string | null;
    toDate: string | null;
  }>({
    fromDate: moment().subtract(7, "days").startOf("day").format("DD-MM-YYYY HH:mm"),
    toDate: moment().format("DD-MM-YYYY HH:mm")
  });

  const [keyFobList, setKeyFobList] = useState<KeyFobList[]>([]);
  const [completionStatusList] = useState(Object.values(RequestStatus) || []);
  const [reqAckStatusList, setReqAckStatusList] = useState<ReqAckStatusList[]>([]);

  const { value: pdfIconLoading, handleDisableValue: handleDisablePdf, handleEnableValue: handleEnablePdf } = useValue(false);

  const { masterFilters, customerList, locationList } = useSelector((state: RootState) => state.common);

  const { value: loading, handleEnableValue: handleEnableLoading, handleDisableValue: handleDisableLoading } = useValue(false);
  const { pagination, handleChangePagination, handleChangeTotalSize, handleResetPagination } = useTablePagination(0);

  /* HANDLERS */
  let startIndex = ((pagination?.current ?? 1) - 1) * (pagination?.pageSize ?? 6);

  const handleModeChange = (data: ParkingRequestTypeConstant) => {
    handleChangeTotalSize(historyList.length);
    setMode(data);
    handleResetPagination();
  };

  const handleChangeOnSearch = (value: string) => {
    handleChangeTotalSize(historyList.length);
    const trimmedValue = value.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    setRefetch(true);
  };

  const handleChangeSearch = (value: string) => {
    handleChangeTotalSize(historyList.length);
    const trimmedValue = value.replace(/^\s+/, "");
    setSearchedText(trimmedValue);
    if (!trimmedValue) {
      setRefetch(true);
    }
  };

  const handleCancel = (recordId: number) => {
    setRecordID(recordId);
  };

  const handleOk = async () => {
    try {
      if (!recordID) return;
      const res = await parkingServices.CancelScheduledList(recordID);
      CustomNotification.showSuccessMessage(res?.message);
      setRefetch(true);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      setRecordID(null);
    }
  };

  const handleChangeFilter = (type: string, value: any) => {
    if (type === "status") {
      setSelectedCompletionStatus(value);
    } else if (type === "keyFob") {
      setSelectedKeyFob(value);
    } else if (type === "reqAckStatus") {
      setSelectedReqAckStatus(value);
    }
  };

  const handleChangeDatePicker = (dates: [Moment | null, Moment | null] | null) => {
    if (!dates || !dates[0] || !dates[1]) {
      setDateRange({ fromDate: null, toDate: null });
      setRefetch(true);
      return;
    }

    const fromDate = dates[0].format("DD-MM-YYYY HH:mm");
    const toDate = dates[1].format("DD-MM-YYYY HH:mm");

    try {
      /* Validate date range using the function */
      General.validateDateRange({ fromDate, toDate });
      setDateRange({ fromDate, toDate });
    } catch (error) {
      CustomNotification.showErrorMessage(error);
      return false;
    }
  };

  const handleGo = () => {
    handleResetPagination();
    setRefetch(true);
  };

  const handleExportPDFFile = async () => {
    try {
      handleEnablePdf();

      if (!masterFilters?.customerId || !masterFilters?.locationId) {
        CustomNotification.showErrorMessage(ErrorMessageConstants.SELECT_CUSTOMER_LOCATION_FILTER);
        return;
      }

      const requestId = General.getRequestId(mode);

      /* Validate DateRange */
      if (dateRange.fromDate && dateRange.toDate) General.validateDateRange(dateRange);

      const customer = customerList?.find((customer) => customer.id === masterFilters?.customerId);
      const location = locationList?.find((location) => location.id === masterFilters?.locationId);
      const reqAckStatus = reqAckStatusList?.find((status: any) => status.id === selectedReqAckStatus);
      const keyFob = selectedKeyFob ? keyFobList?.find((fob) => fob.id === selectedKeyFob) : null;

      const headerInfo: any = {
        header: `${userData?.config?.theme?.HEADER_TEXT || ""} Reports`,
        customerName: `${customer?.customer_name || ""}`,
        locationName: `${location?.location_name || ""}`,
        reportName: "Parking History",
        requestType: mode ?? "",
        selectedCompletionStatus,
        requestAckStatus: reqAckStatus?.request_ack_description ?? "",
        keyFobToken: `${keyFob?.keyFobToken ?? ""}`,
        fromDate: dateRange?.fromDate ?? "-",
        toDate: dateRange?.toDate ?? "-"
      };

      const paramsObject: ParamsObject = {
        requestId,
        searchQuery: searchedText,
        customerId: masterFilters?.customerId,
        locationId: masterFilters?.locationId,
        selectedCompletionStatus,
        selectedKeyFob,
        selectedReqAckStatus,
        fromDate: dateRange?.fromDate,
        toDate: dateRange?.toDate
      };

      const response = await parkingServices.getLogParkingListByRequestId(paramsObject);

      const allVisitorData =
        response?.data?.rows?.map((item, index) => ({
          "Sr No.": index + 1,
          Username: item?.full_name || "",
          KeyFob: `${item?.keyFob?.key_fob_token} - ${item?.keyFob?.vehicleType?.vehicle_type_name}` || "",
          Location: item?.customerLocation?.location_name || "",
          Client: item?.customerClient?.client_name || "",
          "Request Status": item?.requestStatus || "",
          "Request ACK Status": item?.requestACK?.request_ack_description || "",
          "Scheduled Date-Time": item?.scheduled_date_time || "",
          "Created On": item?.created_on || ""
        })) || [];

      ExportFile.pdfFileDownload(allVisitorData, "Parking History Report", headerInfo);
    } catch (error) {
      CustomNotification.showErrorMessage(error);
    } finally {
      handleDisablePdf();
    }
  };

  /* EFFECT */
  useEffect(() => {
    if (mode && pagination?.current && pagination?.pageSize) {
      setRefetch(true);
    } else {
      setRefetch(false);
    }
  }, [mode, pagination?.current, pagination?.pageSize, masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    const fetchParkingHistoryList = async () => {
      try {
        handleEnableLoading();

        const requestId = General.getRequestId(mode);

        /* Validate DateRange */
        if (dateRange.fromDate && dateRange.toDate) General.validateDateRange(dateRange);

        const paramsObject: ParamsObject = {
          requestId,
          searchQuery: searchedText,
          page: pagination?.current || DefaultPagination.PAGE,
          pageSize: pagination?.pageSize || DefaultPagination.PAGE_SIZE,
          customerId: masterFilters?.customerId,
          locationId: masterFilters?.locationId,
          selectedCompletionStatus,
          selectedKeyFob,
          selectedReqAckStatus,
          fromDate: dateRange?.fromDate,
          toDate: dateRange?.toDate
        };

        const response = await parkingServices.getLogParkingListByRequestId(paramsObject);

        const formattedResponse = response?.data?.rows?.map((item, index) => {
          return {
            ...item,
            sr_no: startIndex + index + 1,
            full_name: item.full_name,
            user_name: `${item?.keyFobAllocation?.mobileUser?.first_name ?? ""} ${item?.keyFobAllocation?.mobileUser?.last_name ?? ""}`,
            key_FOB: `${item?.keyFob?.key_fob_token} - ${item?.keyFob?.vehicleType?.vehicle_type_name}`,
            location: item?.customerLocation?.location_name ?? "",
            client: item?.customerClient?.client_name ?? "",
            requestACKStatus: item?.requestACK?.request_ack_description ?? "",
            key_fob: item?.keyFob?.key_fob_token ?? "",
            key_fob_display_value: `${item?.keyFob?.key_fob_token} - ${item?.keyFob?.vehicleType?.vehicle_type_name}`,
            log_parking_request_type_id: item.log_parking_request_type_id
          };
        });

        setHistoryList(formattedResponse);
        handleChangeTotalSize(response?.data?.count || 0);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      } finally {
        setRefetch(false);
        handleDisableLoading();
      }
    };

    if (reFetch) fetchParkingHistoryList();
  }, [reFetch]);

  useEffect(() => {
    const fetchReqAckList = async () => {
      try {
        const response = await commonAPI.getReqAckStatusList();
        setReqAckStatusList(response?.data);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      }
    };

    fetchReqAckList();
  }, []);

  /* fetchKeyFobList */
  useEffect(() => {
    const fetchKeyFobList = async () => {
      try {
        /* Rest of the filters static but keyFob list changes on location Id change */
        setSelectedKeyFob(null);
        setKeyFobList([]);
        if (!masterFilters?.customerId || !masterFilters?.locationId) return;

        const response = await commonAPI.getKeyFobListByCustomerAndLocationId(masterFilters?.customerId, masterFilters?.locationId);
        const formattedResponse = response?.data?.map((item) => {
          return {
            ...item,
            keyFobToken: `${item?.key_fob_token ?? ""} - ${item?.vehicleType?.vehicle_type_name ?? ""}`
          };
        });
        setKeyFobList(formattedResponse);
      } catch (error) {
        CustomNotification.showErrorMessage(error);
      }
    };
    fetchKeyFobList();
  }, [masterFilters?.customerId, masterFilters?.locationId]);

  useEffect(() => {
    setDateRange((prev) => {
      const prevToDate = moment(prev.toDate, "DD-MM-YYYY HH:mm");
      const isToday = prevToDate.isSame(moment(), "day");

      return {
        fromDate: prev.fromDate,
        toDate: isToday ? moment().format("DD-MM-YYYY HH:mm") : prev.toDate
      };
    });
  }, [reFetch]);

  return {
    loading,
    historyList,
    handleChangeOnSearch,
    handleChangeSearch,
    searchedText,
    pagination,
    handleChangePagination,
    handleModeChange,
    recordID,
    handleCancel,
    handleOk,
    mode,
    selectedCompletionStatus,
    selectedKeyFob,
    selectedReqAckStatus,
    handleChangeFilter,
    keyFobList,
    completionStatusList,
    reqAckStatusList,
    handleChangeDatePicker,
    dateRange,
    handleGo,
    handleExportPDFFile,
    pdfIconLoading
  };
};

export default useParkingHistory;
